import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useRef,
  Ref,
} from 'react'
import { Table, Input, Button, Space, TableProps, Popover } from 'antd'
import useDidUpdateEffect from '@/hooks/useDidUpdateEffect'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import TablePopover from './TablePopover'
import microApp from '@micro-zoe/micro-app'

export const withEmptyRender = (columns: any[]) => {
  return columns.map((item) => {
    if (item.render) {
      return item
    } else {
      return {
        ...item,
        render: (text: any, record: any) => {
          if (text === 0) {
            return text
          }
          if (text === 'null') {
            return '-'
          }
          return text || '-'
        },
      }
    }
  })
}

interface Node {
  [key: string]: any
  childrenKey?: Node[]
}

const loop = (list: Node[], childrenKey: string): Node[] => {
  return list.map((n: Node) => {
    if (n[childrenKey] && n[childrenKey].length > 0) {
      return {
        ...n,
        children: n[childrenKey] ? loop(n[childrenKey], childrenKey) : [],
      }
    } else {
      return n
    }
  })
}

interface Pagination {
  current: number
  pageSize: number
}

interface Sorter {
  field?: string
  order?: string
}

interface Filters {
  [key: string]: any[]
}

export const joinTableFilter = (
  filterInfo: Filters
): { [key: string]: string } => {
  const filterParams = Object.keys(filterInfo).reduce(
    (result: { [key: string]: string }, key: string) => {
      const value = filterInfo[key]
      if (value && value.length) {
        result[key] = value.join(',')
      }
      return result
    },
    {}
  )
  return filterParams
}

export const generateTableParams = (
  page: Pagination | null,
  sorter: { sorter: Sorter },
  filters: { filters: Filters }
) => {
  const pageParams = page
    ? {
        pageNumber: page.current,
        pageSize: page.pageSize,
      }
    : {}
  let sorterParams = {}
  const { field, order } = sorter.sorter
  if (field && order) {
    sorterParams = {
      sortName: field,
      sort: order === 'ascend' ? 'asc' : 'desc',
    }
  }
  const filterParams = joinTableFilter(filters.filters)
  return {
    ...pageParams,
    ...sorterParams,
    ...filterParams,
  }
}

const indexedList = (list: object[], pagination?: Pagination): object[] => {
  if (!Array.isArray(list)) return []
  return list.map((item, index) => {
    return {
      ...item,
      uIndex: pagination
        ? index + 1 + (pagination.current - 1) * pagination.pageSize
        : index + 1,
    }
  })
}

const defaultPagination = {
  current: 1,
  pageSize: 10,
}

/**
 *
 * @param {columns} 列表的列配置
 * @param {queryParams} 额外的请求参数
 * @param {indexColumn} 是否显示序号列
 * @param {checkable} 是否显示选择框
 * @param {rowKey} 行的唯一标识
 * @param {onLoad} 请求成功的回调
 * @param {autoRefresh} 是否自动刷新, 默认false,可以填时间间隔
 * @param {allowFetch} 是否允许请求
 * @param {onSelectedRowKeysChange} 选择框变化的回调
 * @param {initialData} 初始数据,非异步请求时使用
 * @param {paramsStore} 是否将请求参数存储到url中
 * @param {childrenKey} 子级展开字段
 * @param {request} 请求方法
 * @param {tableProps} antd Table组件的其他属性
 */

interface PrimeTableProps extends Partial<TableProps<any>> {
  firstReq?:React.MutableRefObject<Boolean>
  firstLoad?:any
  setFirstLoad?:any
  columns?: any[]
  queryParams?: any
  indexColumn?: boolean
  rowKey?: string
  onLoad?: (response: any) => void
  setLoading?: (loading: boolean) => void
  autoRefresh?: boolean
  allowFetch?: boolean
  onSelectedRowKeysChange?: (
    selectedRowKeys: any[],
    selectedRows: any[]
  ) => void
  initialData?: any
  paramsStore?: boolean
  childrenKey?: string
  request?: (params: any) => Promise<any>
  renderCateForItem?: (record: any) => React.ReactNode
  popoverTree?: {
    isRenderCate: boolean
    isMedDRA: boolean
    getLltData: (name: string, code: string, options?: { signal?: AbortSignal }) => Promise<any>
  }
}

interface PopoverState {
  visible: boolean;
  record: any;
  triggerElement: HTMLElement | null;
  isFixed: boolean;
}
const microData = window.microApp?.getData() || {
  quoteData: {},
}
const PrimeTable = (
  {
    firstLoad,
    setFirstLoad,
    columns = [],
    queryParams = {},
    indexColumn = false,
    rowKey = 'id',
    onLoad,
    autoRefresh = false,
    allowFetch = true,
    onSelectedRowKeysChange,
    initialData = null,
    paramsStore = false,
    childrenKey,
    request,
    renderCateForItem,
    popoverTree,
    setLoading: parentSetLoading = () => {},
    ...tableProps
  }: PrimeTableProps,
  ref: Ref<any>
) => {
  const { t } = useTranslation()
  const [data, setData] = useState<object[]>()
  const [searchParams, setSearchParams] = useSearchParams()
  const originPagination = searchParams.get('pagination')
    ? JSON.parse(searchParams.get('pagination') as string)
    : defaultPagination
  const originSorter = searchParams.get('sorter')
    ? JSON.parse(searchParams.get('sorter') as string)
    : {}
  const originFilters = searchParams.get('filters')
    ? JSON.parse(searchParams.get('filters') as string)
    : {}
  const [pagination, setPagination] = useState(originPagination)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [refreshFlag, setRefreshFlag] = useState(false)
  const [filters, setFilters] = useState(originFilters)
  const [sorter, setSorter] = useState(originSorter)
  const [popoverState, setPopoverState] = useState<{
    visible: boolean;
    record: any;
    triggerElement: HTMLElement | null;
    isFixed: boolean;
  }>({
    visible: false,
    record: null,
    triggerElement: null,
    isFixed: false,
  });
  const [hoverPopoverState, setHoverPopoverState] = useState<{
    visible: boolean;
    record: any;
    triggerElement: HTMLElement | null;
    isFixed: boolean;
  }>({
    visible: false,
    record: null,
    triggerElement: null,
    isFixed: false,
  });
  const [hoverRecord, setHoverRecord] = useState<any>(null);
  const [hoverTreeData, setHoverTreeData] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isHoveringRef = useRef(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const responseRef = useRef<Ref<any>>()
  const paramsRef = useRef<Ref<any>>()
  const timer = useRef<Ref<any>>()
  const firstReq = useRef(false)



  const handleSearch = (
    selectedKeys: string[],
    confirm: () => void,
    dataIndex: number
  ) => {
    confirm()
  }
  const handleReset = (clearFilters: () => void) => {
    clearFilters()
  }

  useEffect(() => { parentSetLoading(loading) }, [loading])

  const getColumnSearchProps = (dataIndex: number) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: (selectedKeys: string[]) => void
      selectedKeys: string[]
      confirm: () => void
      clearFilters: () => void
      close: () => void
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          placeholder={t('ikg.input')}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            {t('ikg.search')}
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            {t('ikg.reset')}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined,
        }}
      />
    ),
  })

  const handleRowClick = (record: any, event: React.MouseEvent) => {
    const triggerElement = (event.target as HTMLElement).closest('.ikg-icon-tooltip-triggle');
    if (!triggerElement) return;

    const rect = triggerElement.getBoundingClientRect();
    const x = rect.left - 10;
    const y = rect.top + (rect.height / 2);
    
    setPopoverState({
      visible: true,
      record,
      x,
      y,
      isFixed: true,
    });
    setHoverPopoverState({
      visible: false,
      record: null,
      x: 0,
      y: 0,
      isFixed: false,
    });
  };

  const handlePopoverClose = () => {
    setPopoverState({
      visible: false,
      record: null,
      x: 0,
      y: 0,
      isFixed: false,
    });
  };

  const mergedColumns = columns.map((col) => {
    let item = { ...col }
    if (item.sorter) {
      item.sortOrder = sorter.field === item.dataIndex && sorter.order
    }
    if (item.filters || item.filterSearch) {
      item.filteredValue = filters[item.dataIndex]
    }
    if (item.filterSearch) {
      item = { ...item, ...getColumnSearchProps(item.dataIndex) }
    }
    return item
  })

  const tableColumns = withEmptyRender(
    indexColumn
      ? [
          {
            title: t('ikg.index'),
            dataIndex: 'uIndex',
            width: 80,
          },
          ...mergedColumns,
        ]
      : mergedColumns
  ).map((item: any) => ({
    ...item,
    title: <span style={{ fontWeight: 400 }}>{item.title}</span>,
  }));

  const reload = () => {
    setPagination({ ...defaultPagination })
  }

  const refresh = () => {
    setRefreshFlag(!refreshFlag)
  }

  const onTableChange = (
    pagination: object,
    filters: object,
    sorter: object
  ) => {
    setPagination(pagination)
    setFilters(filters)
    setSorter(sorter)
  }

  useDidUpdateEffect(() => {
    if (!paramsStore) return
    if (pagination) {
      searchParams.set(
        'pagination',
        JSON.stringify({
          current: pagination.current,
          pageSize: pagination.pageSize,
        })
      )
    } else {
      searchParams.delete('pagination')
    }
    if (Object.keys(filters).length > 0) {
      searchParams.set('filters', JSON.stringify(filters))
    } else {
      searchParams.delete('filters')
    }
    if (sorter && sorter.field && sorter.order) {
      searchParams.set(
        'sorter',
        JSON.stringify({
          field: sorter.field,
          order: sorter.order,
        })
      )
    } else {
      searchParams.delete('sorter')
    }
    setSearchParams(searchParams, { replace: true })
  }, [
    JSON.stringify(pagination),
    JSON.stringify(filters),
    JSON.stringify(sorter),
  ])

  const fetchData = async (withLoading = true) => {
    if (timer.current) {
      clearTimeout(timer.current as any)
    }
    if (!allowFetch) {
      return
    }
    if (!request || typeof request !== 'function') {
      return
    }
    if (withLoading) {
      setLoading(true)
    }
    const params = {
      ...generateTableParams(pagination, { sorter }, { filters }),
      ...queryParams,
    }
    paramsRef.current = params
    try {
      const response = await request(params)
      responseRef.current = response as any
      if (Array.isArray(response)) {
        setData(childrenKey ? loop(response, childrenKey) : response)
        setTotal(response.length)
      } else {
        const { content, totalCount } = response as any
        setData(childrenKey ? loop(content, childrenKey) : content)
        setTotal(totalCount)
      }
      setLoading(false)
      typeof onLoad === 'function' && onLoad(response)
    } catch (error: any) {
      setLoading(false)
    }
  }

  const nextPolling = () => {
    let duration = 10 * 1000
    if (typeof autoRefresh === 'number') {
      duration = autoRefresh
    }
    timer.current = setTimeout(() => {
      fetchData(false)
    }, duration) as any
  }

  useEffect(() => {
    fetchData()
  }, [pagination, refreshFlag])

  useDidUpdateEffect(() => {
    if (data?.length === 0 && pagination.current > 1) {
      setPagination({
        ...pagination,
        current: pagination.current - 1,
      })
      return
    }
    if (autoRefresh && (data?.length as number) > 0) {
      nextPolling()
    }
  }, [data])

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current as any)
        timer.current = null
      }
    }
  }, [])
  
  useDidUpdateEffect(() => {
    // 这里巨坑，屎上加屎代码
    if(microData.product === 'ipvs'){
      if(!queryParams.directoryId) return
      if(firstLoad){
        reload()
      }else{
        setFirstLoad(true)
      }
    }else{
      reload()
    }
  }, [JSON.stringify(queryParams)])

  const debouncedMouseEnter = (callback: () => void) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(callback, 100);
  };

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tableRef.current || !popoverTree?.isRenderCate) return;

    const handleMouseEnter = (e: MouseEvent) => {
      const triggerElement = (e.target as HTMLElement).closest('.ikg-icon-tooltip-triggle');
      if (!triggerElement || popoverState.visible) return;

      const row = triggerElement.closest('tr');
      if (!row) return;

      const record = (data as any[])?.find(item => `${item[rowKey]}` === row.getAttribute('data-row-key'));
      if (!record) return;

      isHoveringRef.current = true;
      
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      debouncedMouseEnter(async () => {
        try {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          
          abortControllerRef.current = new AbortController();
          
          setHoverRecord(record);
          
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout')), 5000);
          });
          
          const fetchPromise = popoverTree.getLltData(
            record.name || record.drugName,
            record.code || record.drugRecordNumber,
            { signal: abortControllerRef.current.signal }
          );
          
          const treeData = await Promise.race([fetchPromise, timeoutPromise]);
          
          if (!isHoveringRef.current || abortControllerRef.current?.signal.aborted) {
            return;
          }
          
          if (Array.isArray(treeData) && treeData.length > 0) {
            setHoverTreeData(treeData);
            setHoverPopoverState({
              visible: true,
              record,
              triggerElement,
              isFixed: false,
            });
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            return;
          }
          console.error('Failed to fetch tree data:', error);
        }
      });
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (popoverState.visible) return;

      const triggerElement = (e.target as HTMLElement).closest('.ikg-icon-tooltip-triggle');
      if (!triggerElement) return;

      const relatedTarget = e.relatedTarget as HTMLElement;
      if (relatedTarget?.closest('.ikg-popover')) {
        return;
      }
      
      isHoveringRef.current = false;

      hoverTimeoutRef.current = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        
        setHoverRecord(null);
        setHoverTreeData([]);
        setHoverPopoverState({
          visible: false,
          record: null,
          triggerElement: null,
          isFixed: false,
        });
      }, 100);
    };

    const handleClick = (e: MouseEvent) => {
      const triggerElement = (e.target as HTMLElement).closest('.ikg-icon-tooltip-triggle');
      if (!triggerElement) return;

      const row = triggerElement.closest('tr');
      if (!row) return;

      const record = (data as any[])?.find(item => `${item[rowKey]}` === row.getAttribute('data-row-key'));
      if (!record) return;
      
      setPopoverState({
        visible: true,
        record,
        triggerElement,
        isFixed: true,
      });
      setHoverPopoverState({
        visible: false,
        record: null,
        triggerElement: null,
        isFixed: false,
      });
    };

    tableRef.current.addEventListener('mouseover', handleMouseEnter);
    tableRef.current.addEventListener('mouseout', handleMouseLeave);
    tableRef.current.addEventListener('click', handleClick);

    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('mouseover', handleMouseEnter);
        tableRef.current.removeEventListener('mouseout', handleMouseLeave);
        tableRef.current.removeEventListener('click', handleClick);
      }
    };
  }, [data, popoverTree?.isRenderCate, rowKey, popoverState.visible]);

  useImperativeHandle(ref, () => ({
    refresh,
    reload,
    getParamsWithoutPage: () => {
      const { pageNumber, pageSize, ...rest } = paramsRef.current as any
      return rest
    },
    getParams: () => paramsRef.current,
    getResponse: () => responseRef.current,
    getTableData: () => {
      return {
        data,
        total,
      }
    },
  }))
  return (
    <>
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          dataSource={
            initialData
              ? indexedList(initialData)
              : indexedList(data as any[], pagination)
          }
          rowKey={(record: any) => record[rowKey]}
          pagination={{
            ...pagination,
            total,
            showTotal: (total: number) => `${t('ikg.total', { count: total })}`,
            showSizeChanger: true,
          }}
          onChange={onTableChange}
          scroll={{ x: 'max-content' }}
          loading={loading}
          size="middle"
          style={{ marginTop: 24 }}
          {...tableProps}
        />
      </div>
      {popoverTree && popoverTree.isRenderCate && (hoverPopoverState.visible || popoverState.visible) && (
        <TablePopover
          key={popoverState.visible ? `fixed-${popoverState.record?.[rowKey]}` : `hover-${hoverPopoverState.record?.[rowKey]}`}
          {...(popoverState.visible ? popoverState : hoverPopoverState)}
          onClose={popoverState.visible ? handlePopoverClose : () => {
            setHoverPopoverState({
              visible: false,
              record: null,
              triggerElement: null,
              isFixed: false,
            });
          }}
          treeData={hoverTreeData}
          isMedDRA={popoverTree.isMedDRA}
        />
      )}
    </>
  )
}

export default forwardRef(PrimeTable)
